import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getSocialConnections,
  connectSocialAccount,
  getSocialPosts,
} from '../../services/contentPlugins';
import PluginRenderingBar from './PluginRenderingBar';
import './ContentPlugins.css';

const RENDERING_MESSAGE = 'Generating your plugin image… You can close this window — we\'ll keep working in the background.';
const PROVIDER = 'instagram';

function isSocialTokenInvalidError(err) {
  return err?.response?.data?.code === 'SOCIAL_TOKEN_INVALID';
}

const MODES = [
  { id: 'smart', label: 'AI Pick', desc: 'Let AI choose the best post automatically' },
  { id: 'latest', label: 'Latest', desc: 'Your newest post' },
  { id: 'most_popular', label: 'Most Popular', desc: 'Most liked post' },
  { id: 'trending', label: 'Trending', desc: 'Hottest by recent engagement' },
  { id: 'specific', label: 'Specific Post', desc: 'Pick exact posts to show' },
];

function buildSocialPreviewHtml({ handle, modeLabel }) {
  const safeHandle = String(handle || 'your_brand').replace(/[<>"]/g, '');
  const safeMode = String(modeLabel || 'AI Pick').replace(/[<>"]/g, '');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
*{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,system-ui,sans-serif}
html,body{width:100%;height:100%;overflow:hidden}
body{position:relative;background:#0b0b0f}
.scene{position:absolute;left:50%;top:50%;width:1920px;height:1080px;transform-origin:center center;
background:#0b0b0f;display:flex;align-items:center;justify-content:center}
.card{width:70%;height:82%;background:rgba(20,20,28,.8);border:1px solid rgba(255,255,255,.12);
border-radius:36px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 40px 120px rgba(0,0,0,.5)}
.hd{display:flex;align-items:center;gap:22px;padding:34px 40px}
.av{width:84px;height:84px;border-radius:50%;background:linear-gradient(135deg,#5b5be0,#a23ad6);
border:3px solid rgba(255,255,255,.85)}
.hb{display:flex;flex-direction:column;gap:8px}
.h{color:#fff;font-size:40px;font-weight:700}.h .a{opacity:.6;font-weight:500}
.mb{font-size:24px;font-weight:600;color:#fff;padding:8px 18px;border-radius:999px;background:rgba(255,255,255,.16);width:fit-content}
.pb{margin-left:auto;font-size:26px;font-weight:700;color:#fff;padding:12px 24px;border-radius:999px;
background:linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)}
.md{flex:1;background:linear-gradient(135deg,#23232f,#15151d)}
.cap{color:rgba(255,255,255,.95);font-size:38px;padding:34px 44px;font-weight:500}
</style></head><body>
<div class="scene"><div class="card"><div class="hd"><div class="av"></div>
<div class="hb"><div class="h"><span class="a">@</span>${safeHandle}</div><span class="mb">${safeMode}</span></div>
<span class="pb">Instagram</span></div>
<div class="md"></div>
<p class="cap">Your latest post will appear here, kept up to date automatically.</p>
</div></div>
<script>
(function(){
  function fit(){
    var scene=document.querySelector('.scene');
    if(!scene)return;
    var scale=Math.min(window.innerWidth/1920,window.innerHeight/1080);
    if(!Number.isFinite(scale)||scale<=0)scale=0.1;
    scene.style.transform='translate(-50%,-50%) scale('+scale+')';
  }
  fit();
  window.addEventListener('resize',fit);
  requestAnimationFrame(fit);
})();
</script>
</body></html>`;
}

export default function SocialPluginConfig({
  open,
  onClose,
  onSave,
  userId,
  token,
  initialConfig,
  saving,
  rendering = false,
}) {
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [connectionId, setConnectionId] = useState(null);
  const [mode, setMode] = useState('smart');
  const [posts, setPosts] = useState([]);
  const [selectedPostIds, setSelectedPostIds] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [reconnectPending, setReconnectPending] = useState(false);

  const handleConnectRef = useRef(() => {});

  const loadConnections = useCallback(() => {
    if (!userId) return;
    setLoadingConnections(true);
    getSocialConnections(userId, token, PROVIDER)
      .then((res) => {
        const list = res.data?.connections || [];
        setConnections(list);
        setConnectionId((prev) => prev || (list[0]?.id ?? null));
      })
      .catch(() => setConnections([]))
      .finally(() => setLoadingConnections(false));
  }, [userId, token]);

  useEffect(() => {
    if (!open) return;
    if (initialConfig) {
      setMode(initialConfig.mode || 'smart');
      setConnectionId(initialConfig.connectionId || null);
      setSelectedPostIds(initialConfig.postIds || []);
    }
    loadConnections();
  }, [open, initialConfig, loadConnections]);

  useEffect(() => {
    if (!open) {
      setPosts([]);
      if (!rendering) {
        setSelectedPostIds([]);
      }
    }
  }, [open, rendering]);

  const beginReconnect = useCallback((message) => {
    setConnections([]);
    setConnectionId(null);
    setPosts([]);
    alert(message || 'Your Instagram session expired. Please reconnect your account.');
    setReconnectPending(true);
  }, []);

  // Load posts when entering "specific" mode.
  useEffect(() => {
    if (!open || mode !== 'specific' || !connectionId) {
      return;
    }
    setLoadingPosts(true);
    getSocialPosts({ userId, connectionId, provider: PROVIDER, limit: 18 }, token)
      .then((res) => setPosts(res.data?.posts || []))
      .catch((err) => {
        if (isSocialTokenInvalidError(err)) {
          beginReconnect(err?.response?.data?.error);
          return;
        }
        setPosts([]);
      })
      .finally(() => setLoadingPosts(false));
  }, [open, mode, connectionId, userId, token, beginReconnect]);

  const connectCleanupRef = useRef(null);

  // Tear down any in-flight OAuth listeners/timers when the modal closes or unmounts.
  useEffect(() => {
    if (!open && connectCleanupRef.current) {
      connectCleanupRef.current();
    }
    return () => {
      if (connectCleanupRef.current) connectCleanupRef.current();
    };
  }, [open]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await connectSocialAccount(userId, PROVIDER, token);
      const authUrl = res.data?.authUrl;
      if (!authUrl) throw new Error('No auth URL returned');
      const popup = window.open(authUrl, 'invert-social-connect', 'width=600,height=720');
      if (!popup) {
        setConnecting(false);
        alert('Popup blocked. Please allow popups for this site and try again.');
        return;
      }

      // Track the current connection count so we can detect a new one appearing,
      // independent of postMessage (which COOP headers on the callback can block).
      const baselineCount = connections.length;
      let settled = false;
      let pollTimer = null;
      let closedTicks = 0;

      const finish = (outcome) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        if (pollTimer) clearInterval(pollTimer);
        connectCleanupRef.current = null;
        if (popup && !popup.closed) popup.close();
        setConnecting(false);
        if (outcome === 'success') {
          loadConnections();
        } else if (outcome === 'error') {
          alert('Could not connect the account. Please try again.');
        }
        // 'cancelled' -> silently stop waiting.
      };

      const onMessage = (event) => {
        if (event.data?.type !== 'invert-social-connected') return;
        finish(event.data.status === 'success' ? 'success' : 'error');
      };

      window.addEventListener('message', onMessage);

      // Primary detection: poll the connections endpoint. When a new account is
      // stored server-side, treat it as success (works even if the popup can't
      // message us back or won't self-close).
      pollTimer = setInterval(async () => {
        try {
          const res = await getSocialConnections(userId, token, PROVIDER);
          const list = res.data?.connections || [];
          if (list.length > baselineCount) {
            finish('success');
            return;
          }
        } catch {
          // ignore transient poll errors
        }
        // If the popup is closed and no new connection showed up after a grace
        // period, stop waiting quietly (user likely cancelled).
        if (popup.closed) {
          closedTicks += 1;
          if (closedTicks >= 2) finish('cancelled');
        }
      }, 1500);

      // Allow the close/unmount effect to abort the flow.
      connectCleanupRef.current = () => finish('cancelled');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e?.message || 'Failed to start connection.');
      setConnecting(false);
    }
  };

  handleConnectRef.current = handleConnect;

  useEffect(() => {
    if (!open || !reconnectPending || connecting) return;
    setReconnectPending(false);
    handleConnectRef.current();
  }, [open, reconnectPending, connecting]);

  if (!open) return null;

  const readOnly = rendering;
  const selectedConnection = connections.find((c) => c.id === connectionId);
  const modeLabel = MODES.find((m) => m.id === mode)?.label;
  const previewHtml = buildSocialPreviewHtml({
    handle: selectedConnection?.username,
    modeLabel,
  });

  const canSave = !readOnly
    && connectionId
    && (mode !== 'specific' || selectedPostIds.length > 0);

  // Single-select: one post per slot. Clicking the selected post deselects it.
  const togglePost = (id) => {
    setSelectedPostIds((prev) => (prev.includes(id) ? [] : [id]));
  };

  const handleSave = () => {
    if (!canSave) return;
    const config = {
      provider: PROVIDER,
      source: 'account',
      connectionId,
      mode,
      postCount: 1,
    };
    if (mode === 'specific') config.postIds = selectedPostIds;
    onSave(config);
  };

  return (
    <div className="contentPluginOverlay" role="presentation">
      <div
        className={`contentPluginConfigModal ${readOnly ? 'contentPluginConfigReadOnly' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="contentPluginConfigHeader">
          <h2>Social Media Plugin</h2>
          <button type="button" className="contentPluginClose" onClick={onClose}>×</button>
        </div>
        {rendering ? <PluginRenderingBar message={RENDERING_MESSAGE} /> : null}
        <div className="contentPluginConfigBody">
          <div className="contentPluginForm">
            <label>Connected account</label>
            {loadingConnections ? (
              <p className="contentPluginHint">Loading accounts…</p>
            ) : connections.length === 0 ? (
              <div>
                <p className="contentPluginHint">No Instagram account connected yet.</p>
                <button
                  type="button"
                  className="contentPluginSave"
                  onClick={handleConnect}
                  disabled={connecting || readOnly}
                  style={{ marginTop: 8 }}
                >
                  {connecting ? 'Waiting for Instagram…' : 'Connect Instagram'}
                </button>
              </div>
            ) : (
              <select
                className="contentPluginInput"
                value={connectionId || ''}
                onChange={(e) => setConnectionId(Number(e.target.value))}
                disabled={readOnly}
              >
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>@{c.username}</option>
                ))}
              </select>
            )}

            {connections.length > 0 && (
              <>
                <label style={{ marginTop: 16 }}>What to show</label>
                <div className="contentPluginModeList">
                  {MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`contentPluginModeChoice ${mode === m.id ? 'contentPluginModeChoiceActive' : ''}`}
                      onClick={() => setMode(m.id)}
                      disabled={readOnly}
                    >
                      <span className="contentPluginModeLabel">{m.label}</span>
                      <span className="contentPluginModeDesc">{m.desc}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {mode === 'specific' && connections.length > 0 && (
              <div className="contentPluginPostPicker">
                {loadingPosts ? (
                  <p className="contentPluginHint">Loading posts…</p>
                ) : posts.length === 0 ? (
                  <p className="contentPluginHint">No posts found.</p>
                ) : (
                  <div className="contentPluginPostGrid">
                    {posts.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`contentPluginPostTile ${selectedPostIds.includes(p.id) ? 'contentPluginPostTileActive' : ''}`}
                        onClick={() => togglePost(p.id)}
                        disabled={readOnly}
                        title={p.caption}
                      >
                        <img src={p.mediaUrl} alt="" loading="lazy" />
                        <span className="contentPluginPostStat">♥ {p.likes}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="contentPluginPreviewPane">
            <p className="contentPluginPreviewLabel">Preview</p>
            <div className="contentPluginPreviewFrameWrap">
              <iframe
                title="Social preview"
                className="contentPluginPreviewFrame"
                srcDoc={previewHtml}
                sandbox="allow-scripts"
              />
            </div>
          </div>
        </div>
        <div className={`contentPluginConfigFooter ${rendering ? 'contentPluginConfigFooterRendering' : ''}`}>
          {rendering ? (
            <p className="contentPluginRenderingHint">
              Reopen this slot anytime to check progress. Editing unlocks when the image is ready.
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" className="contentPluginCancel" onClick={onClose}>
              {rendering ? 'Close' : 'Cancel'}
            </button>
            {!rendering ? (
              <button
                type="button"
                className="contentPluginSave"
                disabled={!canSave || saving}
                onClick={handleSave}
              >
                {saving ? 'Starting…' : 'Save Social Plugin'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
